import React, { useEffect, useState } from "react";
import API from "../services/api";

const MAX_IMAGES = 10;

const AddProduct = () => {
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState("");

  const [productName, setProductName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [minOrderQty, setMinOrderQty] = useState("");

  const [existingImages, setExistingImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);

  /* ===========================
     LOAD PRODUCTS
  =========================== */
  const loadProducts = async () => {
    try {
      const res = await API.get("/selfstore/all");
      if (res.data?.success) {
        setProducts(res.data.data || []);
      }
    } catch (err) {
      console.error("Error loading products:", err);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  /* ===========================
     SELECT PRODUCT
  =========================== */
  const handleSelect = (id) => {
    setSelectedId(id);
    const product = products.find((p) => p._id === id);
    if (!product) return;

    setProductName(product.productName || "");
    setDescription(product.description || "");
    setPrice(product.cost || "");
    setMinOrderQty(product.minOrderQty || "");
    setExistingImages(product.imageUrls || []);
    setNewFiles([]);
    setPreviewImages([]);
  };

  /* ===========================
     DRAG & DROP
  =========================== */
  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);

    if (existingImages.length + newFiles.length + files.length > MAX_IMAGES) {
      alert(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    addFiles(files);
  };

  const addFiles = (files) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setNewFiles((prev) => [...prev, ...files]);
    setPreviewImages((prev) => [...prev, ...previews]);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);

    if (existingImages.length + newFiles.length + files.length > MAX_IMAGES) {
      alert(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }

    addFiles(files);
  };

  /* ===========================
     REMOVE IMAGES
  =========================== */
  const removeExistingImage = (index) => {
    const updated = [...existingImages];
    updated.splice(index, 1);
    setExistingImages(updated);
  };

  const removeNewImage = (index) => {
    const updatedPreview = [...previewImages];
    updatedPreview.splice(index, 1);

    const updatedFiles = [...newFiles];
    updatedFiles.splice(index, 1);

    setPreviewImages(updatedPreview);
    setNewFiles(updatedFiles);
  };

  /* ===========================
     RESIZE IMAGE (Canvas)
  =========================== */
  const resizeImage = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = (e) => {
        img.src = e.target.result;
      };

      img.onload = () => {
        const MAX_WIDTH = 1000;
        const scaleSize = MAX_WIDTH / img.width;

        const canvas = document.createElement("canvas");
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/jpeg",
          0.7
        );
      };

      reader.readAsDataURL(file);
    });
  };

  /* ===========================
     UPLOAD IMAGES
  =========================== */
  const uploadImages = async () => {
    const uploadedUrls = [];

    for (let file of newFiles) {
      const resizedBlob = await resizeImage(file);

      console.log("Original:", file.size / 1024 / 1024, "MB");
      console.log("Resized:", resizedBlob.size / 1024 / 1024, "MB");

      const formData = new FormData();
      formData.append("image", resizedBlob, "product.jpg");

      const res = await API.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.success && res.data?.url) {
        uploadedUrls.push({
          imageId: `img_${Date.now()}_${uploadedUrls.length + 1}`,
          url: res.data.url
        });
      }
    }

    return uploadedUrls;
  };

  /* ===========================
     SAVE PRODUCT
  =========================== */
  const handleSave = async () => {
    try {
      let finalImages = [...existingImages];

      if (newFiles.length > 0) {
        const uploaded = await uploadImages();
        finalImages = [...existingImages, ...uploaded];
      }

      if (selectedId) {
        await API.put(`/selfstore/edit/${selectedId}`, {
          productName,
          cost: Number(price),
          description,
          minOrderQty: Number(minOrderQty),
          imageUrls: finalImages,
        });

        alert("Product Updated");
      } else {
        await API.post("/selfstore/add", {
          productName,
          cost: Number(price),
          description,
          productTypes: [],
          imageUrls: finalImages,
        });

        alert("Product Added");
      }

      resetForm();
      loadProducts();
    } catch (err) {
      console.error("Save error:", err);
      alert("Error saving product");
    }
  };

  const resetForm = () => {
    setSelectedId("");
    setProductName("");
    setDescription("");
    setPrice("");
    setMinOrderQty("");
    setExistingImages([]);
    setNewFiles([]);
    setPreviewImages([]);
  };

const handleDelete = async () => {
  if (!selectedId) return;

  const confirmDelete = window.confirm(
    "Are you sure you want to delete this product?"
  );

  if (!confirmDelete) return;

  try {
    await API.delete(`/selfstore/delete/${selectedId}`);

    alert("Product deleted successfully");

    resetForm();
    loadProducts();
  } catch (err) {
    console.error("Delete error:", err);
    alert("Error deleting product");
  }
};

  /* ===========================
     UI
  =========================== */
  return (
    <div
      style={{ marginLeft: "260px", width: "calc(100% - 260px)" }}
      className="min-h-screen bg-gray-100 flex justify-center pt-16 px-6"
    >
      <div className="w-full max-w-3xl bg-white rounded-xl shadow-xl p-10">
        <h2 className="text-2xl font-bold mb-6 text-center">
          Add / Edit Product
        </h2>

        <select
          value={selectedId}
          onChange={(e) => handleSelect(e.target.value)}
          className="w-full p-3 border rounded mb-4"
        >
          <option value="">-- Select Existing Product --</option>
          {products.map((p) => (
            <option key={p._id} value={p._id}>
              {p.productName}
            </option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Product Name"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          className="w-full p-3 border rounded mb-4"
        />

        <textarea
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full p-3 border rounded mb-4"
        />

        <input
          type="number"
          placeholder="Price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-full p-3 border rounded mb-4"
        />

        <input
          type="number"
          placeholder="Min Order Quantity"
          value={minOrderQty}
          onChange={(e) => setMinOrderQty(e.target.value)}
          className="w-full p-3 border rounded mb-4"
        />

        {/* Drag & Drop */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="border-2 border-dashed p-6 text-center mb-4 rounded"
        >
          Drag & Drop Images Here (Max 10)
          <br />
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="mt-2"
          />
        </div>

        {/* Existing Images */}
        {existingImages.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">Existing Images</h4>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {existingImages.map((img, index) => (
                <div
                  key={index}
                  style={{
                    width: "120px",
                    height: "150px",
                    overflow: "hidden",
                    borderRadius: "10px",
                    position: "relative",
                    border: "1px solid #ddd",
                    background: "#f9f9f9"
                  }}
                >
                  <img
                    src={img.url || img}
                    alt="existing"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }}
                  />

                  <button
                    onClick={() => removeExistingImage(index)}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      background: "rgba(255,0,0,0.8)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "22px",
                      height: "22px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* New Images */}
        {previewImages.length > 0 && (
          <>
            <h4 className="font-semibold mb-2">New Images</h4>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              {previewImages.map((src, index) => (
                <div
                  key={index}
                  style={{
                    width: "120px",
                    height: "150px",
                    overflow: "hidden",
                    borderRadius: "10px",
                    position: "relative",
                    border: "1px solid #ddd",
                    background: "#f9f9f9"
                  }}
                >
                  <img
                    src={src}
                    alt="preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover"
                    }}
                  />

                  <button
                    onClick={() => removeNewImage(index)}
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      background: "rgba(255,0,0,0.8)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "22px",
                      height: "22px",
                      cursor: "pointer",
                      fontSize: "12px"
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

<div style={{ display: "flex", gap: "12px", marginTop: "15px" }}>
  <button
    onClick={handleSave}
    style={{
      flex: 1,
      background: "#2e7d32",
      color: "white",
      padding: "12px",
      borderRadius: "8px",
      border: "none",
      fontWeight: "600",
      cursor: "pointer"
    }}
  >
    {selectedId ? "Update Product" : "Add Product"}
  </button>

  {selectedId && (
    <button
      onClick={handleDelete}
      style={{
        flex: 1,
        background: "#c62828",
        color: "white",
        padding: "12px",
        borderRadius: "8px",
        border: "none",
        fontWeight: "600",
        cursor: "pointer"
      }}
    >
      Delete Product
    </button>
  )}
</div>
      </div>
    </div>
  );
};

export default AddProduct;